import React, { useMemo, useEffect, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';

import {
  CardContainer,
  Card,
  CardFront,
  CardBack,
  Label,
  OptionContainer,
  Option,
  Icon,
  StatusLabel,
  HintAndExplanationButtonContainer,
  ButtonsContainer,
  ExplanationHeadingContainer,
  ExplanationContainer,
  ErrorText,
} from './styles';
import { OutlineButton } from 'components/Button/RoundButton';

export default React.memo(
  ({
    quizText,
    userLevel,
    subLevel,
    exercise,
    isCreator,
    testImplId,
    language,
    setQuizCompleted,
    updateFlowProgress,
    inputExercise,
  }) => {
    const savedQuizData = useMemo(() => {
      return inputExercise?.levelData?.[userLevel]?.[language]?.flowProgress?.[subLevel]?.quizData;
    }, [inputExercise?.levelData, userLevel, subLevel, language]);

    const [tagError, setTagError] = useState('');

    const quizTags = useMemo(() => {
      const quizTagRegex = /<p>\[quiz\]<\/p>(.*?)<p>\[\/quiz\]<\/p>/s;
      const match = quizText.match(quizTagRegex);
      if (!match) return setTagError('Quiz tag not handled properly');
      let text;
      if (match && match[1]) text = match[1];

      const tagsList = [
        'question',
        'option',
        'option-correct',
        'feedback-correct',
        'feedback-incorrect',
        'explanation',
        'hint',
      ];

      const unclosedTagRegex =
        /\[(question|option|option-correct|feedback-correct|feedback-incorrect|explanation|hint)\](?![^]*?\[\/\1\])/g;
      const unclosedTags = text.match(unclosedTagRegex);
      if (unclosedTags?.length) {
        const uniqueTags = [...new Set(unclosedTags.map((tag) => tag.match(/\[(\w+)\]/)[1]))];
        const output = `${uniqueTags.join(', ')} ${uniqueTags.length > 1 ? 'tags' : 'tag'} not closed properly`;
        return setTagError(output);
      }

      const tags = {};
      const customTagRegex = /\[([^\]]+?)\](.*?)\[\/\1\]/g;
      const matches = [...text.matchAll(customTagRegex)];
      matches.forEach((match) => {
        const tagName = match[1];
        const tagValue = match[2];
        if (tags[tagName]) {
          if (Array.isArray(tags[tagName])) {
            tags[tagName].push(tagValue);
          } else {
            tags[tagName] = [tags[tagName], tagValue];
          }
        } else {
          tags[tagName] = tagValue;
        }
      });
      if (typeof tags.option === 'string') tags.option = [tags.option];

      // Find missing tags
      const allTagsExist = tagsList.every((tag) => tag in tags);
      if (!allTagsExist && isCreator) {
        const missingTags = tagsList.filter((tag) => !(tag in tags));
        if (missingTags.length) {
          return setTagError(
            `Mentioned ${missingTags.length === 1 ? 'tag is' : 'tags are'} missing from the quiz: ${missingTags.join(', ')}`
          );
        }
      }

      return tags;
    }, [isCreator, quizText]);

    const [isFlipped, setIsFlipped] = useState(false);
    const [options, setOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(savedQuizData?.selectedOption ?? '');
    const [submitted, setSubmitted] = useState(savedQuizData?.submitted ?? false);
    const [showHint, setShowHint] = useState(savedQuizData?.showHint ?? false);
    const [showExplanation, setShowExplanation] = useState(savedQuizData?.showExplanation ?? false);

    let frontSideHeightRef = useRef(null);
    let previousState = useRef({
      selectedOption: savedQuizData?.selectedOption ?? '',
      submitted: savedQuizData?.submitted ?? false,
      showHint: savedQuizData?.showHint ?? false,
      showExplanation: savedQuizData?.showExplanation ?? false,
    });

    useEffect(() => {
      if (savedQuizData?.showExplanation || savedQuizData?.submitted) setQuizCompleted(true);
      else setQuizCompleted(false);
    }, [savedQuizData, setQuizCompleted]);

    useEffect(() => {
      if (!quizTags) return;
      let existingOptions = [...quizTags.option];
      const randomIndex = Math.floor(Math.random() * quizTags.option.length);
      existingOptions.splice(randomIndex, 0, quizTags['option-correct']);
      setOptions(existingOptions);
    }, [quizTags]);

    useEffect(() => {
      if (!quizTags) return;
      let frontSideElement = document.getElementById(`frontSide${quizTags.question}`);
      if (frontSideElement) frontSideHeightRef.current = frontSideElement?.offsetHeight;
      let backSideElement = document.getElementById(`backSide${quizTags.question}`);
      if (backSideElement) backSideElement.style.minHeight = `${frontSideHeightRef.current}px`;
    }, [isFlipped, showHint, showExplanation, quizTags]);

    useEffect(() => {
      if (showExplanation || submitted) setQuizCompleted(true);
    }, [showExplanation, submitted, setQuizCompleted]);

    useEffect(() => {
      if (isCreator) return;
      if (!selectedOption && !showHint && !showExplanation && !submitted) return;
      let currentState = {
        selectedOption: selectedOption,
        submitted: submitted,
        showHint: showHint,
        showExplanation: showExplanation,
      };
      if (JSON.stringify(previousState.current) === JSON.stringify(currentState)) return;

      previousState.current = currentState;
      let userLevelData = inputExercise?.levelData?.[userLevel]?.[language]?.flowProgress
        ? JSON.parse(JSON.stringify(inputExercise?.levelData?.[userLevel]?.[language]?.flowProgress))
        : [];

      if (!userLevelData.length) return;
      if (!userLevelData[subLevel].quizData) userLevelData[subLevel] = { quizData: {} };
      userLevelData[subLevel].quizData = {
        selectedOption,
        showHint,
        showExplanation,
        submitted,
      };
      updateFlowProgress({
        test: exercise,
        language: language,
        level: userLevel,
        data: userLevelData,
      });
    }, [
      submitted,
      showExplanation,
      isCreator,
      exercise,
      userLevel,
      subLevel,
      selectedOption,
      showHint,
      testImplId,
      language,
      updateFlowProgress,
      inputExercise?.levelData,
    ]);

    const getOptionIcon = (option) => {
      if (quizTags['option-correct'] === option && (submitted || showExplanation))
        return { value: <Icon className={'fa fa-check-circle '} />, isCorrect: true };
      if (option === selectedOption) return { value: <Icon className={'fa-regular fa-circle-dot'} /> };
      if (option !== selectedOption) return { value: <Icon className="fa-regular fa-circle" /> };
    };

    return (
      <CardContainer>
        {!quizTags ? (
          <ErrorText>{tagError}</ErrorText>
        ) : (
          <Card isFlipped={isFlipped}>
            {!isFlipped && (
              <CardFront id={`frontSide${quizTags.question}`}>
                <Label dangerouslySetInnerHTML={{ __html: quizTags.question }} />
                {options?.map((option) => {
                  return (
                    <OptionContainer
                      key={option}
                      onClick={() => {
                        if (showExplanation || submitted) return;
                        setSelectedOption(option);
                      }}
                      markedOption={
                        (option === selectedOption && !showExplanation) ||
                        (quizTags['option-correct'] === option && showExplanation)
                      }
                      submitted={submitted || showExplanation}
                    >
                      {getOptionIcon(option).value}
                      <Option dangerouslySetInnerHTML={{ __html: option }} />
                    </OptionContainer>
                  );
                })}
                {showHint && <StatusLabel>Hint: {quizTags.hint}</StatusLabel>}
                {showExplanation && !submitted && <StatusLabel>You saw the answer</StatusLabel>}
                {submitted && getOptionIcon(selectedOption).isCorrect && (
                  <StatusLabel>
                    <Icon isCorrectIncorrect className="fa-regular fa-thumbs-up" />
                    {quizTags['feedback-correct'] ?? 'Correct'}
                  </StatusLabel>
                )}
                {submitted && !getOptionIcon(selectedOption).isCorrect && (
                  <StatusLabel>
                    <Icon isCorrectIncorrect className="fa fa-exclamation-circle" />
                    {quizTags['feedback-incorrect'] ?? 'Incorrect'}
                  </StatusLabel>
                )}
                <ButtonsContainer>
                  <OutlineButton
                    small
                    margin="0 0 auto"
                    disabled={submitted || showExplanation || !selectedOption}
                    onClick={() => {
                      setSubmitted(true);
                      setQuizCompleted(true, quizTags['option-correct'] === selectedOption);
                    }}
                  >
                    Submit
                  </OutlineButton>
                  <HintAndExplanationButtonContainer>
                    {quizTags.hint && (
                      <OutlineButton
                        small
                        margin="0 5px"
                        disabled={submitted || showExplanation || showHint}
                        onClick={() => setShowHint(true)}
                      >
                        Hint
                      </OutlineButton>
                    )}
                    {quizTags.explanation && (
                      <OutlineButton
                        data-tooltip-id={`showExplanation${quizTags.question}`}
                        data-tooltip-content={'This will reveal the answer'}
                        data-tooltip-variant="info"
                        small
                        removeMargin
                        onClick={() => {
                          setShowExplanation(true);
                          setQuizCompleted(true);
                          setIsFlipped(!isFlipped);
                        }}
                      >
                        Show explanation
                      </OutlineButton>
                    )}
                    <Tooltip id={`showExplanation${quizTags.question}`} place="top" className="custom-tooltip" />
                  </HintAndExplanationButtonContainer>
                </ButtonsContainer>
              </CardFront>
            )}
            {isFlipped && (
              <CardBack id={`backSide${quizTags.question}`}>
                <ExplanationHeadingContainer>Explanation</ExplanationHeadingContainer>
                <ExplanationContainer dangerouslySetInnerHTML={{ __html: quizTags.explanation }}></ExplanationContainer>
                <OutlineButton
                  small
                  margin="auto 0px 0px auto"
                  onClick={() => {
                    setShowExplanation(true);
                    setIsFlipped(!isFlipped);
                  }}
                >
                  Back to problem
                </OutlineButton>
              </CardBack>
            )}
          </Card>
        )}
      </CardContainer>
    );
  }
);
